import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../../components/ui/Header';
import NavigationProgress from '../../components/ui/NavigationProgress';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import ConfigurationStep from './components/ConfigurationStep';
import CreativeStudioStep from './components/CreativeStudioStep';
import ConfirmationStep from './components/ConfirmationStep';
import { createLearningTrail, getTrailById, updateLearningTrail, getTrailContent, createTrailContentBatch, deleteAllTrailContent, updateContentOrderBatch, deleteLearningTrail } from '../../services/learningTrailService';
import { useAuth } from '../../contexts/AuthContext';

const LearningJourneyCreator = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [autoSaveStatus, setAutoSaveStatus] = useState('saved');
  const [showExitModal, setShowExitModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Check if we're in edit mode
  const editMode = location?.state?.mode === 'edit';
  const trailId = location?.state?.journeyId;

  const [formData, setFormData] = useState({
    title: '',
    image: '',
    description: '',
    classroom: [],  // now an array of classroom IDs
    minGradeLevel: 'All Grades'
  });

  const [content, setContent] = useState([]);
  const [errors, setErrors] = useState({});

  // Load existing trail data when in edit mode
  useEffect(() => {
    const loadTrailData = async () => {
      if (!editMode || !trailId) {
        return;
      }

      setIsLoading(true);
      try {
        // Fetch trail data
        const { trail, error: trailError } = await getTrailById(trailId);
        
        if (trailError || !trail) {
          console.error('Failed to load trail:', trailError);
          alert('Failed to load trail data. Redirecting to dashboard.');
          navigate('/teacher-dashboard');
          return;
        }

        // Pre-populate form data
        setFormData({
          title: trail?.title || '',
          image: trail?.image_url || '',
          description: trail?.description || '',
          classroom: trail?.trail_classrooms?.map(tc => tc?.classroom_id) || (trail?.classroom_id ? [trail?.classroom_id] : []),
          minGradeLevel: trail?.min_grade_level || 'All Grades'
        });

        // Fetch trail content
        const { contents, error: contentError } = await getTrailContent(trailId);
        
        if (contentError) {
          console.error('Failed to load trail content:', contentError);
        } else if (contents && contents?.length > 0) {
          // Transform content data to match CreativeStudioStep format
          const transformedContent = contents?.map(item => {
            // Flatten the content_data into the root level
            const baseContent = {
              id: item?.id,
              type: item?.content_type,
              orderPosition: item?.order_position,
              pageNumber: item?.page_number || 1,
              timestamp: item?.created_at || new Date()?.toISOString()
            };
            
            // Merge content_data properties at root level
            if (item?.content_data) {
              const contentData = { ...item?.content_data };
              
              // Map database field names to component field names
              if (item?.content_type === 'text' && contentData?.body) {
                contentData.content = contentData?.body;
                delete contentData?.body;
              }
              if (item?.content_type === 'quiz' && contentData?.correct_answer !== undefined) {
                contentData.correctAnswer = contentData?.correct_answer;
                delete contentData?.correct_answer;
              }
              
              return { ...baseContent, ...contentData };
            }
            
            return baseContent;
          });
          setContent(transformedContent);
        }
      } catch (error) {
        console.error('Error loading trail data:', error);
        alert('An error occurred while loading trail data.');
        navigate('/teacher-dashboard');
      } finally {
        setIsLoading(false);
      }
    };

    loadTrailData();
  }, [editMode, trailId, navigate]);

  const steps = [
    {
      id: 'configuration',
      label: 'Configuration',
      description: 'Basic setup'
    },
    {
      id: 'creative-studio',
      label: 'Creative Studio',
      description: 'Add content'
    },
    {
      id: 'confirmation',
      label: 'Review & Publish',
      description: 'Final review'
    }
  ];

  const handleFormChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
    triggerAutoSave();
  };

  const handleImageUpload = (imageData) => {
    setFormData(prev => ({
      ...prev,
      image: imageData
    }));
    if (errors?.image) {
      setErrors(prev => ({
        ...prev,
        image: ''
      }));
    }
    triggerAutoSave();
  };

  const handleContentChange = (newContent) => {
    setContent(newContent);
    
    // If in edit mode and content has database IDs, update order positions
    if (editMode && trailId && newContent?.length > 0) {
      const hasDbIds = newContent?.some(item => typeof item?.id === 'string' && item?.id?.length > 20);
      
      if (hasDbIds) {
        // Debounce the order update to avoid too many database calls
        if (window.orderUpdateTimeout) {
          clearTimeout(window.orderUpdateTimeout);
        }
        
        window.orderUpdateTimeout = setTimeout(async () => {
          const orderUpdates = newContent
            ?.filter(item => typeof item?.id === 'string' && item?.id?.length > 20 && item?.type !== 'page_marker')
            ?.map((item, index) => ({
              id: item?.id,
              orderPosition: index
            }));
          
          if (orderUpdates?.length > 0) {
            const { success, error } = await updateContentOrderBatch(orderUpdates);
            if (error) {
              console.error('Failed to update content order:', error);
            }
          }
        }, 500);
      }
    }
    
    triggerAutoSave();
  };

  const triggerAutoSave = () => {
    setAutoSaveStatus('saving');
    setTimeout(() => {
      setAutoSaveStatus('saved');
    }, 1000);
  };

  const validateStep = (stepIndex) => {
    const newErrors = {};

    if (stepIndex === 0) {
      if (!formData?.title?.trim()) {
        newErrors.title = 'Journey title is required';
      }
      if (!formData?.image) {
        newErrors.image = 'Cover image is required';
      }
      if (!formData?.description?.trim()) {
        newErrors.description = 'Description is required';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, steps?.length - 1));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(prev - 1, 0));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleStepClick = (stepIndex) => {
    if (stepIndex < currentStep) {
      setCurrentStep(stepIndex);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePublish = async (publishData) => {
    try {
      setIsSaving(true);
      
      if (editMode && trailId) {
        // Update existing trail
        const updates = {
          classroomIds: Array.isArray(formData?.classroom) ? formData?.classroom : (formData?.classroom ? [formData?.classroom] : []),
          title: formData?.title,
          description: formData?.description,
          imageUrl: formData?.image,
          minGradeLevel: formData?.minGradeLevel || 'All Grades',
          published: true
        };

        const { trail, error } = await updateLearningTrail(trailId, updates);

        if (error) {
          console.error('Error updating trail:', error);
          alert('Failed to update learning trail. Please try again.');
          return;
        }

        // Delete existing content and save new content
        if (content && content?.length > 0) {
          // Delete old content first
          await deleteAllTrailContent(trailId);
          
          // Save new content
          const { contents, error: contentError } = await createTrailContentBatch(trailId, content);
          
          if (contentError) {
            console.error('Error saving trail content:', contentError);
            alert('Trail updated but failed to save content. Please try again.');
            return;
          }
          
          console.log('Trail content saved:', contents);
        }

        console.log('Trail updated successfully:', trail);
        navigate('/teacher-dashboard', {
          state: {
            message: 'Learning trail updated successfully!',
            journeyTitle: formData?.title
          }
        });
      } else {
        // Create new trail
        const trailData = {
          teacherId: user?.id,
          classroomIds: Array.isArray(formData?.classroom) ? formData?.classroom : (formData?.classroom ? [formData?.classroom] : []),
          title: formData?.title,
          description: formData?.description,
          imageUrl: formData?.image,
          minGradeLevel: formData?.minGradeLevel || 'All Grades',
          published: true
        };

        const { trail, error } = await createLearningTrail(trailData);

        if (error) {
          console.error('Error publishing trail:', error);
          alert('Failed to publish learning trail. Please try again.');
          return;
        }

        // Save trail content
        if (content && content?.length > 0) {
          const { contents, error: contentError } = await createTrailContentBatch(trail?.id, content);
          
          if (contentError) {
            console.error('Error saving trail content:', contentError);
            alert('Trail created but failed to save content. Please try again.');
            return;
          }
          
          console.log('Trail content saved:', contents);
        }

        console.log('Trail published successfully:', trail);
        navigate('/teacher-dashboard', {
          state: {
            message: 'Learning trail published successfully!',
            journeyTitle: formData?.title
          }
        });
      }
    } catch (error) {
      console.error('Error in handlePublish:', error);
      alert('An unexpected error occurred. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveDraft = async () => {
    try {
      setIsSaving(true);
      
      if (editMode && trailId) {
        // Update existing trail as draft
        const updates = {
          classroomIds: Array.isArray(formData?.classroom) ? formData?.classroom : (formData?.classroom ? [formData?.classroom] : []),
          title: formData?.title,
          description: formData?.description,
          imageUrl: formData?.image,
          minGradeLevel: formData?.minGradeLevel || 'All Grades',
          published: false
        };

        const { trail, error } = await updateLearningTrail(trailId, updates);

        if (error) {
          console.error('Error updating draft:', error);
          alert('Failed to update draft. Please try again.');
          return;
        }

        console.log('Draft updated successfully:', trail);
        navigate('/teacher-dashboard', { 
          state: { 
            message: 'Draft updated successfully!',
            journeyTitle: formData?.title
          }
        });
      } else {
        // Create new draft
        const trailData = {
          teacherId: user?.id,
          classroomIds: Array.isArray(formData?.classroom) ? formData?.classroom : (formData?.classroom ? [formData?.classroom] : []),
          title: formData?.title,
          description: formData?.description,
          imageUrl: formData?.image,
          minGradeLevel: formData?.minGradeLevel || 'All Grades',
          published: false
        };

        const { trail, error } = await createLearningTrail(trailData);

        if (error) {
          console.error('Error saving draft:', error);
          alert('Failed to save draft. Please try again.');
          return;
        }

        console.log('Draft saved successfully:', trail);
        navigate('/teacher-dashboard', { 
          state: { 
            message: 'Draft saved successfully!',
            journeyTitle: formData?.title
          }
        });
      }
    } catch (error) {
      console.error('Error in handleSaveDraft:', error);
      alert('An unexpected error occurred. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleExit = () => {
    setShowExitModal(true);
  };

  const handleDeleteTrail = async () => {
    if (!trailId) return;
    try {
      setIsDeleting(true);
      // Delete all trail content first
      await deleteAllTrailContent(trailId);
      // Delete the trail record
      const { success, error } = await deleteLearningTrail(trailId);
      if (error || !success) {
        console.error('Error deleting trail:', error);
        alert('Failed to delete learning trail. Please try again.');
        return;
      }
      navigate('/teacher-dashboard', {
        state: {
          message: 'Learning trail deleted successfully.',
          journeyTitle: formData?.title
        }
      });
    } catch (error) {
      console.error('Error deleting trail:', error);
      alert('An unexpected error occurred while deleting. Please try again.');
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  };

  const confirmExit = () => {
    navigate('/teacher-dashboard');
  };

  // Show loading state while fetching trail data
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-20 pb-12 px-4 md:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto flex items-center justify-center min-h-[60vh]">
            <div className="text-center">
              <Icon name="Loader2" size={48} className="animate-spin text-primary mx-auto mb-4" />
              <p className="text-lg text-muted-foreground">Loading trail data...</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-20 pb-12 px-4 md:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-6 md:mb-8">
            <div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-2">
                {editMode ? 'Edit Learning Trail' : 'Create Learning Trail'}
              </h1>
              <p className="text-sm md:text-base text-muted-foreground">
                {editMode ? 'Update your educational experience' : 'Build engaging educational experiences for your students'}
              </p>
            </div>
            
            <div className="flex items-center space-x-3">
              <div className="hidden md:flex items-center space-x-2 px-3 py-2 bg-card rounded-md border border-border">
                <Icon 
                  name={autoSaveStatus === 'saving' ? 'Loader2' : 'Check'} 
                  size={16} 
                  className={`${autoSaveStatus === 'saving' ? 'animate-spin text-primary' : 'text-success'}`}
                />
                <span className="text-sm text-muted-foreground">
                  {autoSaveStatus === 'saving' ? 'Saving...' : 'All changes saved'}
                </span>
              </div>
              
              {editMode && trailId && (
                <Button variant="destructive" iconName="Trash2" onClick={() => setShowDeleteModal(true)}>
                  Delete Trail
                </Button>
              )}

              <Button variant="outline" iconName="X" onClick={handleExit}>
                Exit
              </Button>
            </div>
          </div>

          <NavigationProgress
            steps={steps}
            currentStep={currentStep}
            onStepClick={handleStepClick}
          />

          <div className="mb-8">
            {currentStep === 0 && (
              <ConfigurationStep
                formData={formData}
                onChange={handleFormChange}
                onImageUpload={handleImageUpload}
                errors={errors}
              />
            )}

            {currentStep === 1 && (
              <CreativeStudioStep
                content={content}
                onChange={handleContentChange}
              />
            )}

            {currentStep === 2 && (
              <ConfirmationStep
                formData={formData}
                content={content}
                onPublish={handlePublish}
                onSaveDraft={handleSaveDraft}
                isPublishing={isSaving}
              />
            )}
          </div>

          <div className="flex items-center justify-between bg-card rounded-lg p-4 md:p-6 shadow-sm">
            <Button
              variant="outline"
              iconName="ChevronLeft"
              iconPosition="left"
              onClick={handlePrevious}
              disabled={currentStep === 0}
            >
              Previous
            </Button>

            <div className="flex items-center space-x-2">
              <span className="text-sm text-muted-foreground">
                Step {currentStep + 1} of {steps?.length}
              </span>
            </div>

            {currentStep < steps?.length - 1 ? (
              <Button
                variant="default"
                iconName="ChevronRight"
                iconPosition="right"
                onClick={handleNext}
              >
                Next Step
              </Button>
            ) : (
              <Button
                variant="success"
                iconName={isSaving ? undefined : "Send"}
                onClick={() => handlePublish({ 
                  ...formData, 
                  content,
                  settings: {
                    publishOption: 'immediate',
                    enableAI: true,
                    allowComments: true,
                    trackProgress: true
                  }
                })}
                disabled={!formData?.title || !formData?.image || !formData?.description || content?.length === 0 || isSaving}
              >
                {isSaving ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Publishing...
                  </span>
                ) : (
                  editMode ? 'Update Journey' : 'Publish Journey'
                )}
              </Button>
            )}
          </div>
        </div>
      </main>
      {showExitModal && (
        <>
          <div 
            className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[100]"
            onClick={() => setShowExitModal(false)}
          />
          <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
            <div className="bg-card rounded-xl shadow-xl max-w-md w-full p-6 stagger-fade-in">
              <div className="flex items-start mb-4">
                <div className="flex-shrink-0 w-12 h-12 bg-warning/10 rounded-full flex items-center justify-center mr-4">
                  <Icon name="AlertTriangle" size={24} className="text-warning" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-1">
                    Exit Without Saving?
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Your progress has been auto-saved, but you may want to save as draft before exiting.
                  </p>
                </div>
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <Button variant="outline" onClick={() => setShowExitModal(false)}>
                  Cancel
                </Button>
                <Button variant="secondary" onClick={handleSaveDraft}>
                  Save Draft
                </Button>
                <Button variant="destructive" onClick={confirmExit}>
                  Exit Anyway
                </Button>
              </div>
            </div>
          </div>
        </>
      )}
      {showDeleteModal && (
        <>
          <div 
            className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[100]"
            onClick={() => !isDeleting && setShowDeleteModal(false)}
          />
          <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
            <div className="bg-card rounded-xl shadow-xl max-w-md w-full p-6 stagger-fade-in">
              <div className="flex items-start mb-4">
                <div className="flex-shrink-0 w-12 h-12 bg-destructive/10 rounded-full flex items-center justify-center mr-4">
                  <Icon name="Trash2" size={24} className="text-destructive" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-1">
                    Delete Learning Trail?
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    This will permanently delete <strong>{formData?.title || 'this trail'}</strong> and all its content. This action cannot be undone.
                  </p>
                </div>
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <Button variant="outline" onClick={() => setShowDeleteModal(false)} disabled={isDeleting}>
                  Cancel
                </Button>
                <Button 
                  variant="destructive" 
                  iconName={isDeleting ? 'Loader2' : 'Trash2'}
                  onClick={handleDeleteTrail}
                  disabled={isDeleting}
                >
                  {isDeleting ? 'Deleting...' : 'Delete Trail'}
                </Button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default LearningJourneyCreator;